import { Component, inject, OnInit } from '@angular/core';
import { MenuController, NavController } from '@ionic/angular/standalone';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { Charity } from 'src/app/core/models/Charity';
import { User } from 'src/app/core/models/User';
import { CharityService } from 'src/app/core/services/charity/charity.service';
import { CharityManagementService } from 'src/app/core/services/charity/charity-management.service';
import { CharityAvatarComponent } from '../charity-avatar/charity-avatar.component';
import { IonContent, IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  camera,
  cardOutline,
  chevronForward,
  close,
  giftOutline,
  logOutOutline,
  personAddOutline,
  peopleOutline,
  person,
  receiptOutline,
  repeatOutline,
  settingsOutline,
  swapHorizontal,
} from 'ionicons/icons';
import { AlertService } from 'src/app/core/services/navigation/alert.service';
import { TabService } from 'src/app/core/services/navigation/tab.service';
import { StateType } from 'src/app/core/models/StateType';
import { CharityRole } from 'src/app/core/models/CharityUser';

@Component({
    selector: 'app-charity-menu',
    templateUrl: './menu-charity.component.html',
    styleUrls: ['./menu-charity.component.scss'],
    imports: [
        IonContent,
        IonIcon,
        CharityAvatarComponent,
    ]
})
export class MenuCharityComponent implements OnInit {
  private readonly subscriptions = new Subscription();
  private menuController = inject(MenuController);

  selectedCharity!: Charity;
  loggedInUser!: User;
  currentUserRole: CharityRole = 'MEMBER';
  _imageUrl: string = 'assets/ic-profile-avatar.svg';
  charityName: string = 'My Charity';

  constructor(
    private authService: AuthService,
    private charityService: CharityService,
    private charityManagementService: CharityManagementService,
    private navController: NavController,
    private alertService: AlertService,
    private tabService: TabService,
  ) {
    addIcons({
      camera,
      cardOutline,
      chevronForward,
      close,
      giftOutline,
      logOutOutline,
      personAddOutline,
      peopleOutline,
      person,
      receiptOutline,
      repeatOutline,
      settingsOutline,
      swapHorizontal,
    });
  }

  ngOnInit() {
    this.getCharityInfo();
    this.loadCurrentUserRole();
  }

  getCharityInfo() {
    this.subscriptions.add(
      this.charityService.charity$.subscribe(charity => {
        if (charity) {
          this.selectedCharity = charity;
          this.charityName = charity.name ?? '';
          if (charity.avatarUrl) {
            this._imageUrl = charity.avatarUrl;
          }
        }
      }),
    );
  }

  private loadCurrentUserRole() {
    this.subscriptions.add(
      this.charityManagementService.charityUser$.subscribe({
        next: (charityUser) => {
          this.currentUserRole = charityUser?.role || 'MEMBER';
        },
        error: () => {
          this.currentUserRole = 'MEMBER';
        }
      })
    );
  }

  get canManageTeam(): boolean {
    return this.currentUserRole === 'OWNER' || this.currentUserRole === 'ADMIN';
  }

  get roleLabel(): string {
    return this.currentUserRole.charAt(0) + this.currentUserRole.slice(1).toLowerCase();
  }

  get canManageDonations(): boolean {
    return this.currentUserRole === 'OWNER' || this.currentUserRole === 'ADMIN';
  }

  get imageUrl(): string {
    return this._imageUrl;
  }

  updateImage(event: File) {
    const id: number = Number(this.selectedCharity.id);
    this._imageUrl = URL.createObjectURL(event);
    const formData: FormData = new FormData();
    formData.append('image', event, event.name);
    this.charityService.changeCharityProfilePic(id, formData).subscribe({
      next: () => {
        this.charityService.setCurrentCharity(id.toString());
      },
      error: () => {
        this.alertService.showAlertMessage(
          'Profile picture was not uploaded',
          'There was an upload error, please try again later.',
        );
      },
    });
  }

  async signOut(): Promise<void> {
    await this.authService.logout();
    this.menuController.close('charity-menu');
    this.navController.navigateRoot('/welcome');
  }

  closeMenu(): void {
    this.menuController.close('charity-menu');
  }

  back() {
    this.navController.back();
  }

  goToItem(item: string) {
    this.menuController.close('charity-menu');
    this.navController.navigateForward(['/charity-role-menu/' + item + '/']).then(r => r);
  }

  goToDonationHistory() {
    this.menuController.close('charity-menu');
    this.navController.navigateForward(`charity/donation-history`);
  }

  goToOrders() {
    this.menuController.close('charity-menu');
    this.navController.navigateForward('/charity/charity-orders').then(r => r);
  }

  goToFollowers() {
    this.menuController.close('charity-menu');
    this.navController.navigateForward(`charity/followers`);
  }

  goToAccountManagement() {
    this.menuController.close('charity-menu');
    this.navController.navigateForward(`/charity/stripe-account`).then(r => r);
  }

  goToRecurringDonations() {
    this.menuController.close('charity-menu');
    this.navController.navigateForward(`/charity/recurring-donations`).then(r => r);
  }

  goToTeamManagement() {
    this.menuController.close('charity-menu');
    this.navController.navigateForward(`/charity/team-management`);
  }

  goToSettings() {
    this.menuController.close('charity-menu');
    this.navController.navigateForward(`/charity/settings`);
  }

  switchToUser() {
    this.menuController.close('charity-menu');
    const lastPage: { url: string; state: StateType } | null = this.tabService.getLastPage('user');
    this.navController.navigateRoot(lastPage?.url || '/main/home', {
      state: lastPage?.state || {},
    });
  }
}
